import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentOutlined,
  BoltOutlined,
  CompareArrowsOutlined,
  FactCheckOutlined,
  GppGoodOutlined,
  ListAltOutlined,
  LockOutlined,
  ReportProblemOutlined,
  ShieldOutlined,
  TaskAltOutlined,
  TrackChangesOutlined,
} from '@mui/icons-material';
import type { ClaimBoundarySummary, DecisionReadinessPayload } from '../services/reportsApi';
import type { SafetyReportOutcomeContext } from './safetyReportOutcomeContext';
import {
  sanitizeExternalLabel,
  EXTERNAL_CRITICAL_EXPOSURE_DEMO,
  EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
  EXTERNAL_RECOMMENDED_ACTION_DEMO,
} from '../domain/externalReview/externalReportLanguage';

const VISUAL_TONES = {
  blue: { accent: '#2563eb', soft: '#eff6ff', border: '#bfdbfe' },
  green: { accent: '#16a34a', soft: '#ecfdf5', border: '#86efac' },
  amber: { accent: '#d97706', soft: '#fffbeb', border: '#fcd34d' },
  red: { accent: '#dc2626', soft: '#fef2f2', border: '#fca5a5' },
  purple: { accent: '#7c3aed', soft: '#f5f3ff', border: '#c4b5fd' },
  neutral: { accent: '#64748b', soft: '#f8fafc', border: '#cbd5e1' },
} as const;

type VisualTone = keyof typeof VISUAL_TONES;
type BadgeTone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

function normalizeOptionalValue(value: unknown): string {
  const raw = String(value ?? '').trim();
  return raw;
}

function formatTokenLabel(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Review pending';
  return raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatSpecifiedValue(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not specified';
  return normalizeOptionalValue(value) || 'Not specified';
}

function formatExternalDisplayValue(
  value: string | null | undefined,
  fallback: string | null,
): string | null {
  const raw = String(value || '').trim();
  if (raw) return raw;
  return fallback;
}

function formatDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not specified';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ISO_TIMESTAMP_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;

function humanizeEmbeddedTimestamps(value?: string | null): string {
  const raw = normalizeOptionalValue(value);
  if (!raw) return 'Not specified';
  return raw.replace(ISO_TIMESTAMP_RE, (match) => formatDateTime(match));
}

function formatZoneDisplayLabel(value?: string | null): string {
  const raw = normalizeOptionalValue(value);
  if (/demo5tes/i.test(raw)) return 'Restricted Forklift Zone';
  return raw;
}

function formatNumericValue(value: unknown, digits = 1): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : digits,
    maximumFractionDigits: digits,
  });
}

function formatRatePerHour(value?: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not available';
  return `${formatNumericValue(value)}/hr`;
}

function formatPercentDelta(value?: number | null): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not available';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumericValue(value)}%`;
}

function decisionBadgeLabel(
  value?: string | null,
  kind?: 'claim' | 'severity' | 'quality' | 'control' | 'priority' | 'action' | 'verification',
): string {
  const normalized = String(value || '').trim().toLowerCase();
  const byKind: Record<string, Record<string, string>> = {
    claim: {
      measured: 'Measured',
      directional: 'Directional signal',
      insufficient: 'More data needed',
      verified: 'Verified',
      not_evaluated: 'Not evaluated',
      workflow_complete: 'Workflow complete',
    },
    severity: {
      serious_injury_or_fatality_potential: 'SIF potential',
      not_classified: 'Needs severity review',
    },
    quality: {
      ready_for_action: 'Action ready',
      needs_review: 'Needs review',
      insufficient_data: 'Insufficient data',
    },
    control: {
      strong: 'Stronger control',
      moderate: 'Moderate control',
      weak: 'Weaker control',
      requires_review: 'Control review',
      not_available: 'Control detail missing',
    },
    priority: {
      high: 'High priority',
      medium: 'Medium priority',
      low: 'Low priority',
      not_available: 'Priority pending',
    },
    action: {
      completed: 'Action present',
      open: 'Action open',
      not_started: 'Action not started',
      not_available: 'Action detail missing',
    },
    verification: {
      verified: 'Verified',
      directional: 'Directional only',
      requires_followup: 'Verify next',
      not_available: 'Verification pending',
    },
  };
  const mapping = byKind[kind || ''] || {};
  if (!normalized) {
    if (kind === 'severity') return 'Exposure review';
    if (kind === 'priority') return 'Follow-up priority';
    return 'Review pending';
  }
  return mapping[normalized] || formatTokenLabel(value);
}

function decisionClaimTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'measured' || normalized === 'verified') return 'success';
  if (normalized === 'directional' || normalized === 'requires_followup') return 'info';
  if (normalized === 'insufficient' || normalized === 'not_available') return 'warning';
  return 'neutral';
}

function decisionSeverityTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'serious_injury_or_fatality_potential' || normalized === 'high') return 'error';
  if (normalized === 'medium' || normalized === 'not_classified') return 'warning';
  return 'neutral';
}

function decisionQualityTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'ready_for_action') return 'success';
  if (normalized === 'needs_review') return 'warning';
  if (normalized === 'insufficient_data') return 'error';
  return 'neutral';
}

function controlStrengthTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'strong') return 'success';
  if (normalized === 'weak') return 'error';
  if (normalized === 'moderate' || normalized === 'requires_review' || normalized === 'not_available') return 'warning';
  return 'neutral';
}

function priorityTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'high') return 'error';
  if (normalized === 'medium') return 'warning';
  return 'neutral';
}

function correctiveActionTone(value?: string | null): BadgeTone {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'completed') return 'success';
  if (normalized === 'open' || normalized === 'not_started') return 'warning';
  return 'neutral';
}

function DecisionBadge(props: { label: string; tone?: BadgeTone }) {
  const styles = {
    success: { color: '#166534', bgcolor: '#DCFCE7', borderColor: '#86EFAC' },
    error: { color: '#B91C1C', bgcolor: '#FEE2E2', borderColor: '#FCA5A5' },
    warning: { color: '#B45309', bgcolor: '#FEF3C7', borderColor: '#FCD34D' },
    info: { color: '#1D4ED8', bgcolor: '#DBEAFE', borderColor: '#93C5FD' },
    neutral: { color: '#475569', bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
  } as const;
  const tone = styles[props.tone || 'neutral'];
  return (
    <Chip
      label={props.label}
      size="small"
      sx={{
        height: 24,
        fontWeight: 700,
        fontSize: '0.72rem',
        color: tone.color,
        bgcolor: tone.bgcolor,
        border: '1px solid',
        borderColor: tone.borderColor,
        '& .MuiChip-label': { px: 1.05 },
      }}
    />
  );
}

function DecisionFieldRow(props: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(124px, 144px) minmax(0, 1fr)' },
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'start',
        py: 0.45,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
        {props.label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          textAlign: 'left',
          minWidth: 0,
          overflowWrap: 'anywhere',
        }}
      >
        {props.value}
      </Typography>
    </Box>
  );
}

function DecisionReadinessCard(props: {
  number: number;
  title: string;
  icon: React.ElementType;
  badgeLabel?: string;
  badgeTone?: BadgeTone;
  accent: VisualTone;
  prominent?: boolean;
  children: React.ReactNode;
}) {
  const Icon = props.icon;
  const tone = VISUAL_TONES[props.accent];
  const prominent = Boolean(props.prominent);
  const isNeutral = props.accent === 'neutral';
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: '1px solid',
        borderColor: isNeutral ? '#E2E8F0' : tone.border,
        boxShadow: 'none',
        background: prominent && !isNeutral ? `linear-gradient(180deg, ${tone.soft} 0%, #FFFFFF 82%)` : '#FFFFFF',
      }}
    >
      <CardContent sx={{ p: prominent ? 2.3 : 2 }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.disabled', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 0.5 }}
        >
          {`Step ${props.number} of 9`}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1.3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: prominent ? 34 : 30,
                height: prominent ? 34 : 30,
                borderRadius: '50%',
                bgcolor: tone.soft,
                color: tone.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <Icon aria-hidden sx={{ fontSize: prominent ? 20 : 18 }} />
            </Box>
            <Typography component="h3" variant={prominent ? 'subtitle1' : 'subtitle2'} sx={{ fontWeight: 700 }}>
              {props.title}
            </Typography>
          </Stack>
          {props.badgeLabel ? <DecisionBadge label={props.badgeLabel} tone={props.badgeTone} /> : null}
        </Stack>
        {props.children}
      </CardContent>
    </Card>
  );
}

function ImpactInfoCallout(props: { message: string }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>
      {props.message}
    </Typography>
  );
}

export interface DecisionReadinessSectionProps {
  decisionReadiness?: DecisionReadinessPayload | null;
  canonicalClaimBoundary?: ClaimBoundarySummary | null;
  outcomeContext?: SafetyReportOutcomeContext | null;
  impactDenominatorTrusted: boolean;
  impactMeasurementWaived: boolean;
  showWhatChangedRates: boolean;
  showWhatChangedAuditRates: boolean;
  whatChangedSummaryText: string;
  whatChanged?: DecisionReadinessPayload['what_changed'];
  impactBaselineRatePerHour: number | null;
  impactFollowupRatePerHour: number | null;
  decisionMissingInputsDisplay: string[];
  decisionWarnings: string[];
  externalSafe?: boolean;
}

export function DecisionReadinessSection(props: DecisionReadinessSectionProps) {
  const [showDetailedSteps, setShowDetailedSteps] = useState(false);
  const externalSafe = Boolean(props.externalSafe);
  const data = props.decisionReadiness || null;
  const whatChanged = props.whatChanged || data?.what_changed || null;
  const criticalExposure = data?.severe_exposure || null;
  const decisionQuality = data?.decision_quality || null;
  const riskDriver = data?.risk_driver || null;
  const concentration = data?.concentration || null;
  const controlStrengthReview = data?.control_strength_review || null;
  const recommendedNextAction = data?.recommended_next_action || null;
  const correctiveAction = data?.corrective_action || null;
  const effectivenessVerification = data?.effectiveness_verification || null;
  const canonicalClaimBoundary = props.canonicalClaimBoundary || null;
  const canonicalClaimBoundaryText = normalizeOptionalValue(canonicalClaimBoundary?.wording);
  const outcomeContext = props.outcomeContext || null;
  const isDemoSource = Boolean(outcomeContext?.isDemoOrLoopedSource);

  const impactNotReady = !isDemoSource && !props.impactDenominatorTrusted && !props.impactMeasurementWaived;
  const impactBadgeValue = isDemoSource
    ? 'not_evaluated'
    : impactNotReady
    ? 'insufficient'
    : whatChanged?.claim_strength;
  const impactMainText = isDemoSource
    ? outcomeContext?.decisionImpactMainText || 'Exposure reduction not evaluated on this demo source.'
    : props.impactMeasurementWaived
    ? 'Impact measurement was waived for this report.'
    : impactNotReady
    ? 'Impact is not ready for interpretation.'
    : formatSpecifiedValue(
        externalSafe
          ? sanitizeExternalLabel(props.whatChangedSummaryText, { isDemo: isDemoSource })
          : props.whatChangedSummaryText,
      );
  const impactSupportingText = isDemoSource
    ? outcomeContext?.decisionImpactSupportingText || ''
    : props.impactMeasurementWaived
    ? 'Exposure-rate comparison is not presented for this Signed Safety Scan.'
    : impactNotReady
    ? 'Observation-time reliability requires review before stronger claims can be made.'
    : 'Directional exposure context is retained without claiming corrective-action effectiveness.';

  const recommendedActions = Array.isArray(recommendedNextAction?.actions)
    ? recommendedNextAction?.actions || []
    : [];
  const visibleActions = recommendedActions.slice(0, 4);
  const hiddenActionCount = Math.max(recommendedActions.length - visibleActions.length, 0);

  const missingInputs = props.decisionMissingInputsDisplay;
  const visibleMissingInputs = missingInputs.slice(0, 3);
  const hiddenMissingInputCount = Math.max(missingInputs.length - visibleMissingInputs.length, 0);

  const correctiveActionExists = correctiveAction?.exists === true;
  const correctiveActionMainText = !correctiveActionExists
    ? 'Corrective action not found in signed-off workflow data.'
    : correctiveAction?.exists && correctiveAction?.status === 'completed'
      && Array.isArray(correctiveAction?.missing_fields) && correctiveAction.missing_fields.length
    ? externalSafe
      ? EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP
      : 'Corrective action completed in workflow. Additional detail is needed to assess control strength and verify effectiveness.'
    : formatSpecifiedValue(
        externalSafe
          ? sanitizeExternalLabel(correctiveAction?.summary, { isDemo: isDemoSource })
          : correctiveAction?.summary,
      );

  const controlLevelKey = String(controlStrengthReview?.current_control_level || '').trim().toLowerCase();
  const actionTypeText = normalizeOptionalValue(correctiveAction?.action_type);
  const currentControlLabel = actionTypeText
    ? actionTypeText.toLowerCase() === 'training'
      ? 'Training / administrative'
      : formatTokenLabel(actionTypeText)
    : controlLevelKey && controlLevelKey !== 'unknown'
    ? formatTokenLabel(controlStrengthReview?.current_control_level)
    : externalSafe
    ? 'Review pending'
    : 'Not specified';

  const verificationMainText = isDemoSource
    ? outcomeContext?.effectivenessVerificationText || ''
    : canonicalClaimBoundaryText
    || normalizeOptionalValue(effectivenessVerification?.summary)
    || 'Effectiveness is not verified yet.';

  const criticalExposureText = externalSafe
    ? formatExternalDisplayValue(
        sanitizeExternalLabel(criticalExposure?.summary, { isDemo: isDemoSource }),
        isDemoSource ? EXTERNAL_CRITICAL_EXPOSURE_DEMO : null,
      )
    : formatSpecifiedValue(criticalExposure?.summary);
  const recommendedActionText = externalSafe
    ? formatExternalDisplayValue(
        sanitizeExternalLabel(recommendedNextAction?.summary, { isDemo: isDemoSource }),
        isDemoSource ? EXTERNAL_RECOMMENDED_ACTION_DEMO : null,
      )
    : formatSpecifiedValue(recommendedNextAction?.summary);

  const decisionStatusMain = normalizeOptionalValue(decisionQuality?.summary)
    || 'Follow-up action is supported, but effectiveness is not verified.';

  const fieldOrReview = (value?: string | null) => {
    const text = normalizeOptionalValue(value);
    return text || 'Requires review';
  };
  const riskPatternRows = [
    { label: 'Primary driver', value: fieldOrReview(riskDriver?.safety_rule || riskDriver?.event_category) },
    { label: 'Hotspot', value: fieldOrReview(formatZoneDisplayLabel(concentration?.top_zone)) },
    { label: 'Peak period', value: fieldOrReview(concentration?.top_time_period) },
    { label: 'Category', value: fieldOrReview(concentration?.top_event_category || riskDriver?.event_category) },
  ];

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
      <CardContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography component="h2" variant="h6" gutterBottom sx={{ mb: 0.5 }}>
              {externalSafe ? 'Follow-up readiness' : 'Decision workflow'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {externalSafe
                ? 'What the signed-off scan supports for follow-up review without implying corrective-action effectiveness.'
                : 'What the signed-off scan supports, what remains unverified, and what action should happen next.'}
            </Typography>
          </Box>
          {externalSafe ? null : (
          <Chip icon={<LockOutlined />} label="Signed-off data only" size="small" variant="outlined" />
          )}
        </Stack>

        {!data ? (
          <Alert severity="info">Decision-readiness details were not returned for this Signed Safety Scan.</Alert>
        ) : (
          <>
            <Box
              data-testid="decision-readiness-grid"
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <DecisionReadinessCard
                number={1}
                title="Outcome status"
                icon={CompareArrowsOutlined}
                badgeLabel={decisionBadgeLabel(impactBadgeValue, 'claim')}
                badgeTone={decisionClaimTone(impactBadgeValue)}
                accent="amber"
                prominent
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {impactMainText}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {impactSupportingText}
                </Typography>
                {props.showWhatChangedRates ? (
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    <DecisionFieldRow label="Baseline rate" value={formatRatePerHour(whatChanged?.baseline_rate_per_hour)} />
                    <DecisionFieldRow label="Follow-up rate" value={formatRatePerHour(whatChanged?.followup_rate_per_hour)} />
                    <DecisionFieldRow label="Percent change" value={formatPercentDelta(whatChanged?.change_percent)} />
                  </Stack>
                ) : props.showWhatChangedAuditRates ? (
                  <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                    <DecisionFieldRow label="Baseline rate (audit context)" value={formatRatePerHour(props.impactBaselineRatePerHour)} />
                    <DecisionFieldRow label="Follow-up rate (audit context)" value={formatRatePerHour(props.impactFollowupRatePerHour)} />
                  </Stack>
                ) : null}
                {isDemoSource ? (
                  <ImpactInfoCallout message={outcomeContext?.productionMeasurementLabel || 'Production measurement supported on live data'} />
                ) : (impactNotReady || props.showWhatChangedAuditRates) ? (
                  <ImpactInfoCallout message="Observation-time reliability requires review." />
                ) : null}
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={2}
                title="Critical exposure"
                icon={ReportProblemOutlined}
                badgeLabel={decisionBadgeLabel(criticalExposure?.severity, 'severity')}
                badgeTone={decisionSeverityTone(criticalExposure?.severity)}
                accent="red"
                prominent
              >
                {criticalExposureText ? (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {criticalExposureText}
                  </Typography>
                ) : null}
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={3}
                title="Recommended action"
                icon={BoltOutlined}
                badgeLabel={decisionBadgeLabel(recommendedNextAction?.priority, 'priority')}
                badgeTone={priorityTone(recommendedNextAction?.priority)}
                accent="green"
                prominent
              >
                {recommendedActionText ? (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                    {recommendedActionText}
                  </Typography>
                ) : null}
                {visibleActions.length ? (
                  <Box component="ul" sx={{ mt: 1.1, mb: 0, pl: 2.1 }}>
                    {visibleActions.slice(0, 2).map((action, index) => (
                      <Box component="li" key={`${action?.label || 'action'}-${index}`} sx={{ mb: 0.45 }}>
                        <Typography variant="body2">{formatSpecifiedValue(action?.label)}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null}
                {(() => {
                  const extraActionCount = Math.max(visibleActions.length - 2, 0) + hiddenActionCount;
                  return extraActionCount > 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                      {`+${extraActionCount} more action${extraActionCount === 1 ? '' : 's'}`}
                    </Typography>
                  ) : null;
                })()}
              </DecisionReadinessCard>

              {externalSafe ? null : (
              <Box sx={{ gridColumn: { xs: '1 / -1', xl: '1 / -1' }, display: 'flex', justifyContent: 'flex-start' }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowDetailedSteps((open) => !open)}
                  sx={{ textTransform: 'none', fontWeight: 600, px: 0.5 }}
                >
                  {showDetailedSteps ? 'Hide detailed workflow checks' : 'Show detailed workflow checks (steps 4–9)'}
                </Button>
              </Box>
              )}

              {externalSafe ? null : (
              <Collapse in={showDetailedSteps} sx={{ gridColumn: '1 / -1' }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                }}
              >
              <DecisionReadinessCard
                number={4}
                title="Decision status"
                icon={FactCheckOutlined}
                badgeLabel={decisionBadgeLabel(decisionQuality?.level, 'quality')}
                badgeTone={decisionQualityTone(decisionQuality?.level)}
                accent="neutral"
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {decisionStatusMain}
                </Typography>
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={5}
                title="Risk pattern"
                icon={TrackChangesOutlined}
                accent="neutral"
              >
                {normalizeOptionalValue(riskDriver?.summary) ? (
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                    {formatSpecifiedValue(riskDriver?.summary)}
                  </Typography>
                ) : null}
                <Stack spacing={0.35}>
                  {riskPatternRows.map((row) => (
                    <DecisionFieldRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </Stack>
                {normalizeOptionalValue(concentration?.summary) ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>
                    {formatSpecifiedValue(concentration?.summary)}
                  </Typography>
                ) : null}
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={6}
                title="Control review"
                icon={ShieldOutlined}
                badgeLabel={decisionBadgeLabel(controlStrengthReview?.status, 'control')}
                badgeTone={controlStrengthTone(controlStrengthReview?.status)}
                accent="neutral"
              >
                <Stack spacing={0.35}>
                  <DecisionFieldRow label="Current control" value={currentControlLabel} />
                  <DecisionFieldRow label="Assessment" value={formatSpecifiedValue(controlStrengthReview?.summary)} />
                  <DecisionFieldRow label="Recommended stronger controls" value={formatSpecifiedValue(controlStrengthReview?.recommended_higher_control)} />
                  <DecisionFieldRow label="Reason" value={formatSpecifiedValue(controlStrengthReview?.rationale)} />
                </Stack>
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={7}
                title="Corrective action"
                icon={TaskAltOutlined}
                badgeLabel={correctiveActionExists ? decisionBadgeLabel(correctiveAction?.status, 'action') : undefined}
                badgeTone={correctiveActionTone(correctiveAction?.status)}
                accent="neutral"
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {correctiveActionMainText}
                </Typography>
                {correctiveActionExists ? (
                  <Stack spacing={0.35} sx={{ mt: 1.1 }}>
                    <DecisionFieldRow label="Owner" value={formatSpecifiedValue(correctiveAction?.owner)} />
                    <DecisionFieldRow label="Action type" value={formatSpecifiedValue(correctiveAction?.action_type)} />
                    <DecisionFieldRow label="Due date" value={correctiveAction?.due_date ? formatDateTime(correctiveAction.due_date) : 'Not specified'} />
                    <DecisionFieldRow label="Completed date" value={correctiveAction?.completed_at ? formatDateTime(correctiveAction.completed_at) : 'Not specified'} />
                    <DecisionFieldRow label="Evidence of completion" value={formatSpecifiedValue(correctiveAction?.evidence_of_completion)} />
                    <DecisionFieldRow label="Verification method" value={formatSpecifiedValue(correctiveAction?.verification_method)} />
                  </Stack>
                ) : null}
                {normalizeOptionalValue(correctiveAction?.notes) ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.1 }}>
                    {formatSpecifiedValue(correctiveAction?.notes)}
                  </Typography>
                ) : null}
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={8}
                title="Follow-up verification"
                icon={GppGoodOutlined}
                badgeLabel={decisionBadgeLabel(effectivenessVerification?.status, 'verification')}
                badgeTone={decisionClaimTone(effectivenessVerification?.status)}
                accent="neutral"
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {verificationMainText}
                </Typography>
                <Stack spacing={0.35} sx={{ mt: 1.1 }}>
                  <DecisionFieldRow label="Required method" value={formatSpecifiedValue(effectivenessVerification?.method)} />
                  <DecisionFieldRow label="Baseline window" value={humanizeEmbeddedTimestamps(effectivenessVerification?.baseline_window)} />
                  <DecisionFieldRow label="Follow-up window" value={humanizeEmbeddedTimestamps(effectivenessVerification?.followup_window)} />
                  <DecisionFieldRow label="Claim boundary" value={formatSpecifiedValue(canonicalClaimBoundaryText || effectivenessVerification?.claim_boundary)} />
                </Stack>
              </DecisionReadinessCard>

              <DecisionReadinessCard
                number={9}
                title="What is still needed"
                icon={ListAltOutlined}
                badgeLabel={missingInputs.length ? String(missingInputs.length) : undefined}
                badgeTone="warning"
                accent="neutral"
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {isDemoSource
                    ? 'Exposure-reduction measurement requires live production source context.'
                    : missingInputs.length
                    ? 'Additional inputs are needed before a stronger claim can be made.'
                    : 'No material decision inputs are currently missing.'}
                </Typography>
                {visibleMissingInputs.length ? (
                  <Box component="ul" sx={{ mt: 1.1, mb: 0, pl: 2.1 }}>
                    {visibleMissingInputs.map((item, index) => (
                      <Box component="li" key={`${item}-${index}`} sx={{ mb: 0.45 }}>
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    ))}
                  </Box>
                ) : null}
                {hiddenMissingInputCount > 0 ? (
                  <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.75 }}>
                    {`+${hiddenMissingInputCount} more input${hiddenMissingInputCount === 1 ? '' : 's'}`}
                  </Typography>
                ) : null}
                {props.decisionWarnings.length ? (
                  <Box sx={{ mt: 1.2 }}>
                    {props.decisionWarnings.map((warning, index) => (
                      <Typography key={`${warning}-${index}`} variant="caption" color="text.secondary" sx={{ display: 'block', mt: index === 0 ? 0 : 0.55 }}>
                        {warning}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </DecisionReadinessCard>
              </Box>
              </Collapse>
              )}
            </Box>

            <Box
              sx={{
                mt: 2,
                px: 1.5,
                py: 1.2,
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <AssignmentOutlined aria-hidden sx={{ fontSize: 16, color: '#64748B', mt: '2px' }} />
                <Typography variant="caption" color="text.secondary">
                  Decision conclusions are based on the signed-off data and applicable validation gates. No improvement, worsening, accident prevention, ROI, causality, or corrective-action effectiveness claim is made unless validation gates support it.
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
